const trimTrailingSlash = (value?: string | null) =>
  value ? value.replace(/\/+$/, '') : value || undefined

const FIRST_DEFINED = <T>(...values: (T | undefined)[]): T | undefined =>
  values.find((value): value is T => value !== undefined)

const deriveApiBaseFromGraphqlEnv = (): string | undefined => {
  const graphqlUrl = FIRST_DEFINED(
    process.env.NEXT_PUBLIC_HTTP_LINK_GRAPHQL,
    process.env.HTTP_LINK_GRAPHQL,
    process.env.GRAPHQL_URL
  )

  if (!graphqlUrl || !/^https?:\/\//.test(graphqlUrl)) {
    return undefined
  }

  return trimTrailingSlash(graphqlUrl.replace(/\/graphql\/?$/, ''))
}

/**
 * Detect if running on a demo domain and derive the correct API URL.
 * Demo domains follow the patterns:
 *   - Production: demo-N.trueloyalty.ai -> api-demo-N.trueloyalty.ai
 *   - Staging: demo-N.staging.trueloyalty.ai -> api-demo-N.staging.trueloyalty.ai
 * API endpoints use hyphen (not dot) for wildcard cert coverage.
 */
const getDemoApiUrl = (): string | undefined => {
  if (typeof window === 'undefined') return undefined

  const hostname = window.location.hostname

  // Production demo: demo-N.trueloyalty.ai
  const prodMatch = hostname.match(/^demo-(\d+)\.trueloyalty\.ai$/)
  if (prodMatch) {
    return `https://api-demo-${prodMatch[1]}.trueloyalty.ai`
  }

  // Staging demo: demo-N.staging.trueloyalty.ai
  const stagingMatch = hostname.match(/^demo-(\d+)\.staging\.trueloyalty\.ai$/)
  if (stagingMatch) {
    return `https://api-demo-${stagingMatch[1]}.staging.trueloyalty.ai`
  }

  return undefined
}

/**
 * Check if we're on a demo domain. When true, we should NOT use
 * build-time environment variables since they point to prod/staging.
 */
const isOnDemoDomain = (): boolean => {
  if (typeof window === 'undefined') return false
  const hostname = window.location.hostname
  return /^demo-\d+\.trueloyalty\.ai$/.test(hostname) ||
         /^demo-\d+\.staging\.trueloyalty\.ai$/.test(hostname)
}

export const resolveHttpGraphqlUrl = (): string | undefined => {
  // Check for demo domain first (runtime detection)
  const demoApiUrl = getDemoApiUrl()
  if (demoApiUrl) {
    return `${demoApiUrl}/graphql`
  }

  // If on a demo domain but getDemoApiUrl returned undefined (SSR),
  // don't use build-time env vars - return undefined and let client re-resolve
  if (isOnDemoDomain()) {
    return undefined
  }

  const direct = FIRST_DEFINED(
    process.env.NEXT_PUBLIC_HTTP_LINK_GRAPHQL,
    process.env.HTTP_LINK_GRAPHQL,
    process.env.GRAPHQL_URL
  )

  if (direct) {
    return direct
  }

  const base = FIRST_DEFINED(
    trimTrailingSlash(process.env.NEXT_PUBLIC_API_URL),
    trimTrailingSlash(process.env.API_URL)
  )

  return base ? `${base}/graphql` : undefined
}

export const resolveWsGraphqlUrl = (httpUrl: string | undefined): string | undefined => {
  // Check for demo domain first
  const demoApiUrl = getDemoApiUrl()
  if (demoApiUrl) {
    return demoApiUrl.replace('https://', 'wss://') + '/graphql'
  }

  if (process.env.NEXT_PUBLIC_WS_LINK_GRAPHQL) {
    return process.env.NEXT_PUBLIC_WS_LINK_GRAPHQL
  }

  if (!httpUrl) return undefined

  if (httpUrl.startsWith('https://')) {
    return httpUrl.replace(/^https:\/\//, 'wss://')
  }
  if (httpUrl.startsWith('http://')) {
    return httpUrl.replace(/^http:\/\//, 'ws://')
  }

  return undefined
}

/**
 * Resolve the API base URL for server-side API routes.
 * Uses request host header for demo domain detection.
 */
export const resolveApiUrl = (requestHost?: string): string => {
  // Check for demo domain from request host
  if (requestHost) {
    // Production demo: demo-N.trueloyalty.ai
    const prodMatch = requestHost.match(/^demo-(\d+)\.trueloyalty\.ai/)
    if (prodMatch) {
      return `https://api-demo-${prodMatch[1]}.trueloyalty.ai`
    }

    // Staging demo: demo-N.staging.trueloyalty.ai
    const stagingMatch = requestHost.match(/^demo-(\d+)\.staging\.trueloyalty\.ai/)
    if (stagingMatch) {
      return `https://api-demo-${stagingMatch[1]}.staging.trueloyalty.ai`
    }
  }

  // Fall back to environment variable
  return trimTrailingSlash(process.env.NEXT_PUBLIC_API_URL) ||
    trimTrailingSlash(process.env.API_URL) ||
    deriveApiBaseFromGraphqlEnv() ||
    'https://api.staging.trueloyalty.ai'
}

/** Server-side session routes call the extracted session owner only in the
 * separately built observation product. Browser GraphQL still uses its BFF. */
export const resolveStaffAccessUrl = (requestHost?: string): string => {
  if (process.env.NEXT_PUBLIC_GKE_OBSERVATION !== 'true') return resolveApiUrl(requestHost)
  if (!requestHost || !/^[a-z0-9-]+\.gke-staging\.trueloyalty\.ai(?::\d+)?$/.test(requestHost)) {
    throw new Error('Observation staff access requires its separate hostname')
  }
  const url = process.env.STAFF_ACCESS_INTERNAL_URL
  if (url !== 'http://staff-access-api.true-loyalty-gke-staging.svc.cluster.local:8080') {
    throw new Error('Observation staff access endpoint is not configured')
  }
  return url
}

/**
 * Client-side API base URL for REST calls (e.g. /restaurants/:id/servers).
 * Derives from the GraphQL HTTP URL by stripping /graphql.
 */
export const getApiBaseUrlForClient = (): string | undefined => {
  const graphqlUrl = resolveHttpGraphqlUrl()
  if (!graphqlUrl) return undefined
  return trimTrailingSlash(graphqlUrl.replace(/\/graphql\/?$/, ''))
}
